import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { corridoApi } from "@/lib/corrido-api";

// RenderTargetToggle — which GPU renders: RunPod, or this studio machine.
//
// The switch itself was always one URL (services/corrido-v2/config.js builds
// `${apiBase}/${endpointId}`, and local_worker.py impersonates RunPod's job API
// in front of a local ComfyUI). What was missing is that the URL came from an
// environment variable, so changing it meant editing .env and restarting Node —
// and on the live site, editing Render's environment and waiting for a redeploy.
//
// Two rules this component follows, both learned the hard way:
//
//  1. It reports whether the target can ACTUALLY take a job, not just what it is
//     set to. A toggle that says "My GPU" while the local worker is not running
//     produces a storyboard that dies minutes later, and "the studio machine is
//     offline" has already cost this project a debugging session.
//  2. It refuses to switch to a dead target. The server 409s and this shows the
//     exact command to bring it up, rather than letting a render start doomed.

type Health = {
  reachable: boolean;
  ms?: number;
  status?: number;
  detail?: string;
  workers?: Record<string, number> | null;
};

type Target = {
  mode: "runpod" | "local";
  api_base: string;
  endpoint_id: string;
  source: "env" | "override";
  since?: string | null;
  ready: boolean;
  health: Health;
  options: string[];
};

const AUTH = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function RenderTargetToggle() {
  const { t } = useTranslation();
  const [target, setTarget] = useState<Target | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // The route is admin-only. A 401/403 means this account never sees the
  // toggle, so the poll stops instead of asking again every 20s.
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(corridoApi("/v2/render_target"), { headers: AUTH() });
      if (res.status === 401 || res.status === 403) setDenied(true);
      else if (res.ok) setTarget(await res.json());
    } catch {
      /* transient; the poll will retry */
    }
  }, []);

  useEffect(() => {
    if (denied) return;
    load();
    // The local worker can die at any time — a stale green dot is the failure
    // this component exists to prevent, so re-probe rather than trust the mount.
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [load, denied]);

  const switchTo = async (mode: "runpod" | "local") => {
    if (busy || !target || target.mode === mode) return;
    setBusy(true);
    setProblem(null);
    setHint(null);
    try {
      const res = await fetch(corridoApi("/v2/render_target"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...AUTH() },
        body: JSON.stringify({ mode }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setProblem(body.message || t("studio.couldNotSwitch", { mode }));
        setHint(body.hint || null);
      } else {
        setTarget(body);
      }
    } catch (err) {
      setProblem(String((err as Error)?.message || err));
    } finally {
      setBusy(false);
      load();
    }
  };

  if (!target || denied) return null;

  const dot = target.ready ? "#4ade80" : "#f87171";
  const label = target.mode === "local" ? t("studio.localGpu") : "RunPod";

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", gap: 8,
        padding: "10px 12px", borderRadius: 12,
        background: "#1c1c1e", border: "1px solid #2e2e32",
        fontSize: 12, color: "#d4d4d8", minWidth: 232,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          width: 8, height: 8, borderRadius: 999, background: dot,
          boxShadow: `0 0 6px ${dot}`, flexShrink: 0,
        }} />
        <span style={{ fontWeight: 600, color: "#f4f4f5" }}>{t("studio.renderingOn", { target: label })}</span>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {(["runpod", "local"] as const).map((m) => {
          const active = target.mode === m;
          return (
            <button
              key={m}
              onClick={() => switchTo(m)}
              disabled={busy || active}
              style={{
                flex: 1, padding: "6px 8px", borderRadius: 8, cursor:
                  active || busy ? "default" : "pointer",
                border: `1px solid ${active ? "#52525b" : "#2e2e32"}`,
                background: active ? "#2a2a2e" : "transparent",
                color: active ? "#f4f4f5" : "#a1a1aa",
                fontSize: 12, fontWeight: active ? 600 : 400,
              }}
            >
              {m === "runpod" ? "RunPod" : t("studio.myGpu")}
            </button>
          );
        })}
      </div>

      <div style={{ color: "#71717a", fontSize: 11, lineHeight: 1.5 }}>
        {target.ready
          ? (target.health.ms ? t("studio.readyMs", { ms: target.health.ms }) : t("studio.ready"))
          : target.health.detail || t("studio.notAnswering")}
        {target.source === "env" && t("studio.fromEnvironment")}
      </div>

      {problem && (
        <div style={{ color: "#fca5a5", fontSize: 11, lineHeight: 1.5 }}>
          {problem}
          {hint && (
            <code style={{
              display: "block", marginTop: 6, padding: "6px 8px",
              background: "#141416", borderRadius: 6, color: "#d4d4d8",
              fontSize: 10, whiteSpace: "pre-wrap", wordBreak: "break-all",
            }}>{hint}</code>
          )}
        </div>
      )}
    </div>
  );
}
